import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useServices, useSEO } from "../../hooks";
import ServiceCard from "../services/ServiceCard";
import {
  FiSearch,
  FiArrowRight,
  FiShield,
  FiStar,
  FiClock,
  FiCheckCircle,
  FiDroplet,
  FiSmile,
} from "react-icons/fi";
import "./HomePage.css";

const STEPS = [
  {
    icon: <FiSearch />,
    title: "Choose a Service",
    desc: "Pick from 20+ professional services — cleaning, laptop repair, printer repair, electrician, and more.",
  },
  {
    icon: <FiClock />,
    title: "Book a Slot",
    desc: "Select a convenient date and time. Same-day slots often available.",
  },
  {
    icon: <FiDroplet />,
    title: "Expert Arrives",
    desc: "A verified, trained cleaning expert shows up on time with all equipment.",
  },
  {
    icon: <FiSmile />,
    title: "Pay After Service",
    desc: "Inspect the work and pay only after you're satisfied. Zero upfront.",
  },
];

const TRUST_STATS = [
  { value: "50,000+", label: "Happy Customers" },
  { value: "200+", label: "Verified Cleaners" },
  { value: "4.7★", label: "Average Rating" },
  { value: "₹0", label: "Upfront Payment" },
];

const POPULAR_TAGS = [
  "Car Wash",
  "Bike Wash",
  "Chimney Cleaning",
  "Washing Machine",
  "Laptop Repair",
  "Printer Repair",
  "Electrician",
  "Dress Iron",
  "Hair Cutting",
];

const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Home Services in Kolkata",
  "provider": {
    "@type": "LocalBusiness",
    "name": "ExpertAtDoor",
    "url": "https://www.expertatdoor.com"
  },
  "areaServed": { "@type": "City", "name": "Kolkata" },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Home Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Car Wash" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Bike Wash" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Chimney Cleaning" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Laptop Repair" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Printer Repair" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Electrician Service" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Dress Ironing" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Hair Cutting" } }
    ]
  }
};

const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Do I have to pay before the service?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. At ExpertAtDoor you pay only after the service is completed and you are satisfied. Zero upfront payment."
      }
    },
    {
      "@type": "Question",
      "name": "Are the service experts verified?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. All ExpertAtDoor technicians and cleaners are background-verified and professionally trained before being assigned to jobs."
      }
    },
    {
      "@type": "Question",
      "name": "Which areas in Kolkata do you serve?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We currently serve all major areas across Kolkata, West Bengal. Book online and a verified expert will arrive at your doorstep."
      }
    },
    {
      "@type": "Question",
      "name": "What services does ExpertAtDoor offer?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "ExpertAtDoor offers 20+ home services including car wash, bike wash, chimney cleaning, washing machine service, laptop repair, printer repair, electrician, dress ironing, and hair cutting."
      }
    }
  ]
};

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const { services, loading: svcLoading } = useServices({
    city: "kolkata",
    featured: "true",
    limit: 6,
  });

  useSEO({
    title: "Home Services in Kolkata — Cleaning, Repair & More",
    description:
      "Book trusted home services in Kolkata — cleaning, laptop repair, printer repair, electrician, car wash & more. 50,000+ happy customers. Pay only after service. 4.7★ rated.",
    canonical: "/kolkata",
    jsonLd: HOME_JSON_LD,
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim())
      navigate(`/services?q=${encodeURIComponent(searchQuery)}`);
    else navigate("/services");
  };

  return (
    <div className="homepage">
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="hero" aria-label="Hero — book home services in Kolkata">
        <div className="hero-bg-pattern" aria-hidden="true" />
        <div className="container hero-content">
          <div className="hero-text">
            <span className="hero-eyebrow">Kolkata's #1 Cleaning Service</span>
            <h1>
              A Cleaner Home,
              <br />
              <span className="hero-highlight">At Your Doorstep</span>
            </h1>
            <p className="hero-subtext">
              Professional cleaners, repair technicians, and licensed
              electricians for your home, kitchen, car, laptop, printer, and
              more. Pay only after the work is done — guaranteed.
            </p>

            <form className="hero-search" onSubmit={handleSearch} role="search" aria-label="Search home services">
              <div className="hero-search-inner">
                <FiSearch className="search-icon" size={18} aria-hidden="true" />
                <input
                  type="search"
                  name="q"
                  placeholder="What needs cleaning? e.g. bathroom, sofa, car..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search services"
                />
                <button type="submit" className="btn btn-primary">
                  Search
                </button>
              </div>
            </form>

            <nav className="hero-tags" aria-label="Popular service categories">
              {POPULAR_TAGS.map((tag) => (
                <button
                  key={tag}
                  className="hero-tag"
                  onClick={() =>
                    navigate(`/services?q=${encodeURIComponent(tag)}`)
                  }
                  aria-label={`Browse ${tag} services`}
                >
                  {tag}
                </button>
              ))}
            </nav>
          </div>

          <div className="hero-visual" aria-hidden="true">
            <img
              src="ead.jpeg"
              alt="ExpertAtDoor professional cleaning service in Kolkata"
              className="hero-image"
              style={{ borderRadius: 20 }}
              width="600"
              height="450"
              loading="eager"
              fetchpriority="high"
            />
          </div>
        </div>
      </section>

      {/* ── Trust stats ───────────────────────────────────── */}
      <section className="trust-strip" aria-label="Trust statistics">
        <div className="container">
          <div className="trust-grid">
            {TRUST_STATS.map(({ value, label }) => (
              <div key={label} className="trust-item">
                <span className="trust-value">{value}</span>
                <span className="trust-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Services ─────────────────────────────── */}
      <section className="section" aria-labelledby="popular-services-heading">
        <div className="container">
          <div className="section-header">
            <span className="overline">Most Booked</span>
            <h2 id="popular-services-heading">Popular Cleaning Services</h2>
            <p>What Kolkata's homeowners are booking this week.</p>
          </div>

          <div className="services-grid" role="list" aria-label="Featured services">
            {svcLoading
              ? Array(6)
                  .fill(0)
                  .map((_, i) => (
                    <div
                      key={i}
                      className="card skeleton"
                      style={{ height: 280 }}
                      aria-hidden="true"
                    />
                  ))
              : services.map((svc) => (
                  <ServiceCard key={svc._id} service={svc} />
                ))}
          </div>

          <div className="section-cta">
            <button
              className="btn btn-outline btn-lg"
              onClick={() => navigate("/services")}
              aria-label="View all home services in Kolkata"
            >
              View All Services <FiArrowRight size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────── */}
      <section
        className="section"
        style={{ background: "var(--brand-surface-2)" }}
        aria-labelledby="how-it-works-heading"
      >
        <div className="container">
          <div className="section-header">
            <span className="overline">Simple Process</span>
            <h2 id="how-it-works-heading">How ExpertAtDoor Works</h2>
          </div>

          <ol className="steps-grid" aria-label="Booking process steps">
            {STEPS.map((step, i) => (
              <li key={i} className="step-card">
                <div className="step-number" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="step-icon" aria-hidden="true">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Why us ────────────────────────────────────────── */}
      <section className="section why-section" aria-labelledby="why-us-heading">
        <div className="container">
          <div className="why-grid">
            <div className="why-text">
              <span className="overline">Why Choose Us</span>
              <h2 id="why-us-heading">Cleaning You Can Actually Trust</h2>
              <ul className="why-points" aria-label="Reasons to choose ExpertAtDoor">
                {[
                  {
                    icon: <FiShield aria-hidden="true" />,
                    text: "All cleaners background-verified & trained",
                  },
                  {
                    icon: <FiCheckCircle aria-hidden="true" />,
                    text: "Pay only after service — zero upfront payment",
                  },
                  {
                    icon: <FiClock aria-hidden="true" />,
                    text: "On-time arrival — or we reschedule for free",
                  },
                  {
                    icon: <FiStar aria-hidden="true" />,
                    text: "Transparent pricing, no hidden charges",
                  },
                ].map(({ icon, text }) => (
                  <li key={text} className="why-point">
                    <span className="why-icon">{icon}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
              <button
                className="btn btn-primary btn-lg"
                onClick={() => navigate("/services")}
                aria-label="Book a cleaning service now in Kolkata"
              >
                Book a Cleaning Now <FiArrowRight size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="why-visual" aria-hidden="true">
              <div className="why-badge why-badge-1">
                <FiCheckCircle /> Background Verified
              </div>
              <div className="why-badge why-badge-2">
                <FiStar /> Top Rated ★ 4.7
              </div>
              <div className="why-center-circle">
                <span>₹0</span>
                <small>Upfront Payment</small>
              </div>
              <div className="why-badge why-badge-3">
                <FiShield /> Insured Work
              </div>
              <div className="why-badge why-badge-4">
                <FiClock /> On-Time Arrival
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ Section (boosts SEO + FAQPage schema) ─────── */}
      <section className="section" aria-labelledby="faq-heading" style={{ background: "var(--brand-surface-2)" }}>
        <div className="container">
          <div className="section-header">
            <span className="overline">Have Questions?</span>
            <h2 id="faq-heading">Frequently Asked Questions</h2>
          </div>
          <div className="steps-grid" itemScope itemType="https://schema.org/FAQPage">
            {FAQ_JSON_LD.mainEntity.map((faq, i) => (
              <div key={i} className="step-card" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 itemProp="name">{faq.name}</h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p itemProp="text">{faq.acceptedAnswer.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ────────────────────────────────────── */}
      <section className="cta-banner" aria-label="Call to action — book a service">
        <div className="container">
          <div className="cta-inner">
            <span className="cta-label">Book in 60 seconds</span>
            <h2>Ready for a Cleaner Home?</h2>
            <p>
              Join 50,000+ Kolkata homeowners who trust ExpertAtDoor. Pay only
              after the job is done.
            </p>
            <div className="cta-buttons">
              <button
                className="btn btn-primary btn-lg"
                onClick={() => navigate("/services")}
                aria-label="Browse all home services"
              >
                Browse Services
              </button>
              <button
                className="btn btn-outline btn-lg"
                onClick={() => navigate("/register")}
                aria-label="Create a free ExpertAtDoor account"
              >
                Create Free Account
              </button>
            </div>
          </div>
        </div>

        {/* Stats strip — bridges CTA into footer */}
        <div className="cta-stats">
          {[
            { value: "50K+", label: "Happy Customers" },
            { value: "4.7★", label: "Average Rating" },
            { value: "1,200+", label: "Jobs Completed" },
            { value: "100%", label: "Pay After Service" },
          ].map(({ value, label }) => (
            <div key={label} className="cta-stat">
              <span className="cta-stat-value">{value}</span>
              <span className="cta-stat-label">{label}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
